import type { PaigeClient } from '@paige/api-client';
import type {
  Campaigns,
  Friction,
  Funnel,
  RetentionView,
  Revenue,
  RevenueSort,
  Today,
  Usage,
} from './types';
import { unwrap } from './http';

/**
 * The admin reads that show a period. Every one takes the same two UTC days, `from` and `until`, both
 * inclusive (paige-v1.yaml, AdminFrom and AdminUntil), from the admin area's one period control.
 */
export interface PeriodQuery {
  from: string;
  until: string;
}

export interface UsageQuery extends PeriodQuery {
  feature?: string | undefined;
  screen?: string | undefined;
}

/** What the Funnel screen is narrowed to; `partner` is a code, or `none` for companies without one. */
export interface FunnelQuery extends PeriodQuery {
  source?: string | undefined;
  campaign?: string | undefined;
  partner?: string | undefined;
}

/** Revenue's per-company table is sorted and paged by the ledger: the page only says what it wants. */
export interface RevenueQuery extends PeriodQuery {
  sort: RevenueSort;
  dir: 'asc' | 'desc';
  offset: number;
  limit: number;
}

export interface PeriodApi {
  /** What changed in the business: the period against the one before. */
  getToday(period: PeriodQuery): Promise<Today>;
  getRevenue(query: RevenueQuery): Promise<Revenue>;
  /** What people do and where they struggle (R10 step 4); a feature or screen opens its companies. */
  getUsage(query: UsageQuery): Promise<Usage>;
  getFriction(period: PeriodQuery): Promise<Friction>;
  getFunnel(query: FunnelQuery): Promise<Funnel>;
  /** `partner` is a code, or `none` for companies without one. */
  getCampaigns(period: PeriodQuery, partner?: string): Promise<Campaigns>;
  /** Do companies come back, and for how long? (R10 step 5.) */
  getRetention(period: PeriodQuery): Promise<RetentionView>;
}

/** Only what was asked for: an absent filter is left off the URL, never sent empty. */
function given<T extends object>(query: T): Given<T> {
  return Object.fromEntries(
    Object.entries(query).filter(([, v]) => v !== undefined && v !== ''),
  ) as Given<T>;
}

type Given<T> = { [K in keyof T]: Exclude<T[K], undefined> };

export function periodApi(client: PaigeClient): PeriodApi {
  return {
    async getToday({ from, until }) {
      const r = await client.GET('/admin/today', { params: { query: { from, until } } });
      return unwrap(r) as Today;
    },
    async getRevenue(query) {
      return unwrap(await client.GET('/admin/revenue', { params: { query } })) as Revenue;
    },
    async getUsage(query) {
      const r = await client.GET('/admin/usage', { params: { query: given(query) } });
      return unwrap(r) as Usage;
    },
    async getFriction({ from, until }) {
      const r = await client.GET('/admin/friction', { params: { query: { from, until } } });
      return unwrap(r) as Friction;
    },
    async getFunnel(query) {
      const r = await client.GET('/admin/funnel', { params: { query: given(query) } });
      return unwrap(r) as Funnel;
    },
    async getCampaigns({ from, until }, partner) {
      const query = given({ from, until, partner });
      return unwrap(await client.GET('/admin/campaigns', { params: { query } })) as Campaigns;
    },
    async getRetention({ from, until }) {
      const r = await client.GET('/admin/retention', { params: { query: { from, until } } });
      return unwrap(r) as RetentionView;
    },
  };
}
